import { Box, Divider, Stack, Typography } from "@mui/material";

import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { useTranslation } from "@/translations";

import { AppointmentTargetResult as AppointmentTargetResultModel } from "../AppointmentTargetCalculatorDialog.types";
import { FULL_TIME_WEEKLY_HOURS, TRANSLATION_BASE } from "../AppointmentTargetCalculatorDialog.utils";

export function AppointmentTargetResult({ result }: { result: AppointmentTargetResultModel | null }) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const num = (value: number, decimalScale: number) =>
    new Intl.NumberFormat(language, {
      minimumFractionDigits: decimalScale,
      maximumFractionDigits: decimalScale,
    }).format(value);

  return (
    <Stack
      flex={1}
      gap={2}
      aria-live="polite"
      sx={{
        p: 2,
        bgcolor: "fsw.blue-5",
        borderRadius: 1,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {t(`${TRANSLATION_BASE}.calculationTitle`)}
      </Typography>

      {!result ? (
        <Typography color="text.secondary">{t(`${TRANSLATION_BASE}.result.empty`)}</Typography>
      ) : (
        <>
          <Stack gap={1}>
            <BreakdownRow
              emphasized
              label={t(`${TRANSLATION_BASE}.result.effectiveWorkdays`)}
              value={num(result.effectiveWorkdays, 0)}
            />
            <BreakdownRow
              label={t(`${TRANSLATION_BASE}.result.workdaysPerWeek`)}
              value={num(result.workdaysPerWeek, 0)}
            />

            <Divider />

            <BreakdownRow
              emphasized
              label={t(`${TRANSLATION_BASE}.result.weeksPerMonth`)}
              value={num(result.weeksPerMonth, 2)}
            />
            <BreakdownRow
              label={t(`${TRANSLATION_BASE}.result.hoursFactor`, {
                hours: num(result.weeklyHours, Number.isInteger(result.weeklyHours) ? 0 : 1),
                base: FULL_TIME_WEEKLY_HOURS,
              })}
              value={num(result.hoursFactor, 2)}
            />
          </Stack>

          <Stack gap={1.5}>
            <TargetCard
              caption={t(`${TRANSLATION_BASE}.result.appointmentsLabel`)}
              label={t(`${TRANSLATION_BASE}.result.target1b`)}
              value={num(result.target1b, 0)}
            />
            <TargetCard
              caption={t(`${TRANSLATION_BASE}.result.appointmentsLabel`)}
              label={t(`${TRANSLATION_BASE}.result.targetEg`)}
              value={num(result.targetEg, 0)}
            />
          </Stack>

          <Divider sx={{ borderStyle: "dashed" }} />

          <Typography variant="caption" color="text.secondary">
            {t(`${TRANSLATION_BASE}.result.footnote`, {
              base: FULL_TIME_WEEKLY_HOURS,
              target1b: num(result.target1bPerWeek, 0),
              targetEg: num(result.targetEgPerWeek, 0),
              exact1b: num(result.exact1b, 1),
              exactEg: num(result.exactEg, 1),
            })}
          </Typography>
        </>
      )}
    </Stack>
  );
}

function BreakdownRow({ label, value, emphasized }: { label: string; value: string; emphasized?: boolean }) {
  const fontWeight = emphasized ? "bold" : undefined;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}>
      <Typography color={emphasized ? "text.primary" : "text.secondary"} fontWeight={fontWeight}>
        {label}
      </Typography>
      <Typography fontWeight={fontWeight}>{value}</Typography>
    </Box>
  );
}

function TargetCard({ caption, label, value }: { caption: string; label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 1,
        borderLeft: "4px solid",
        borderLeftColor: "fsw.red",
      }}
    >
      <Box>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
        <Typography variant="subtitle1" fontWeight="bold">
          {label}
        </Typography>
      </Box>
      <Typography variant="h1" color="primary">
        {value}
      </Typography>
    </Box>
  );
}
